import { useContext } from 'react';
import { AppDialogContext } from '@/components/ui/AppDialogProvider';

/**
 * Imperative replacements for window.confirm / alert.
 *
 * Setup: <AppDialogProvider> is mounted once near the root in App.js.
 *
 * Usage anywhere:
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Update shift time?',
 *     description: 'You selected Morning.',
 *     confirmLabel: 'Apply Change',
 *     beforeAfter: { label: 'Time', before: '09:00–17:00', after: '06:00–14:00' },
 *     infoNote: 'This will not affect billing, payroll, invoices, claims, or funding.',
 *   });
 *   if (!ok) return;
 *
 *   const danger = useAppDialog().dangerConfirm;
 *   const result = await danger({
 *     title: 'Cancel this shift?',
 *     reasonRequired: true,
 *     confirmLabel: 'Yes, cancel',
 *   });
 *   if (!result) return;       // false on cancel
 *   // result.reason holds the typed reason
 */
export function useAppDialog() {
  const ctx = useContext(AppDialogContext);
  if (!ctx) throw new Error('useAppDialog must be used inside <AppDialogProvider>');
  return ctx;
}

export const useConfirm = () => useAppDialog().confirm;
export const useAlert   = () => useAppDialog().alert;
